import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import {
  messengerService,
  type ListChatsParams,
  type SendMessageInput,
} from '@/modules/messenger/services/messenger.service'
import { useDebounce } from '@/hooks/useDebounce'
import type {
  ChatListItem,
  ChatMessage,
  ChatReactionSummary,
  ChatUserRef,
  CursorPage,
} from '@/modules/messenger/types/messenger.types'

export const messengerKeys = {
  all: ['messenger'] as const,
  chats: () => [...messengerKeys.all, 'chats'] as const,
  chatsList: (params: ListChatsParams) => [...messengerKeys.chats(), params] as const,
  chatDetail: (chatId: string) => [...messengerKeys.all, 'chat', chatId] as const,
  unread: () => [...messengerKeys.all, 'unread'] as const,
  messages: (chatId: string) => [...messengerKeys.all, 'messages', chatId] as const,
  search: (chatId: string, q: string) => [...messengerKeys.all, 'search', chatId, q] as const,
}

/** Minimum trimmed query length before a search request fires — below this,
 * results are simply cleared client-side (no request). */
const SEARCH_MIN_LENGTH = 2

/** Header chrome badge — polled lightly and refreshed on every chat.* live
 * event (see chat-realtime.ts), same "one number" pattern as the bell. */
export function useMessengerUnreadCount(enabled: boolean) {
  return useQuery({
    queryKey: messengerKeys.unread(),
    queryFn: () => messengerService.unreadCount(),
    enabled,
    staleTime: 15_000,
  })
}

export function useStaffDirectory(enabled: boolean) {
  return useQuery({
    queryKey: [...messengerKeys.all, 'staff'] as const,
    queryFn: () => messengerService.listStaff(),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function useChats(params: ListChatsParams, enabled = true) {
  return useQuery({
    queryKey: messengerKeys.chatsList(params),
    queryFn: () => messengerService.listChats(params),
    enabled,
  })
}

export function useChat(chatId: string | undefined) {
  return useQuery({
    queryKey: messengerKeys.chatDetail(chatId ?? ''),
    queryFn: () => messengerService.getChat(chatId!),
    enabled: Boolean(chatId),
  })
}

/** Newest-first cursor pagination — page 0 is the latest ~30 messages;
 * scrolling up fetches older pages. Never loads the whole chat at once. */
export function useMessages(chatId: string | undefined) {
  return useInfiniteQuery({
    queryKey: messengerKeys.messages(chatId ?? ''),
    queryFn: ({ pageParam }) => messengerService.listMessages(chatId!, { cursor: pageParam }),
    enabled: Boolean(chatId),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
}

/** In-thread search — debounced (≥300ms) and gated at a 2-char minimum so
 * short/empty queries never fire a request. Newest match first, a separate
 * result list rather than the reverse-cursor thread pagination. */
export function useSearchChatMessages(chatId: string | undefined, q: string) {
  const debouncedQ = useDebounce(q.trim(), 300)
  const enabled = Boolean(chatId) && debouncedQ.length >= SEARCH_MIN_LENGTH
  return useQuery({
    queryKey: messengerKeys.search(chatId ?? '', debouncedQ),
    queryFn: () => messengerService.searchMessages(chatId!, { q: debouncedQ }),
    enabled,
  })
}

export function useOpenDirectChat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => messengerService.openDirect(userId),
    onSuccess: (chat) => {
      qc.setQueryData(messengerKeys.chatDetail(chat.id), chat)
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
    },
  })
}

export function useCreateGroupChat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ name, memberIds }: { name: string; memberIds: string[] }) =>
      messengerService.createGroup(name, memberIds),
    onSuccess: (chat) => {
      qc.setQueryData(messengerKeys.chatDetail(chat.id), chat)
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
    },
  })
}

export function useUpdateChat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ chatId, name }: { chatId: string; name: string }) => messengerService.updateChat(chatId, name),
    onSuccess: (chat) => {
      qc.setQueryData(messengerKeys.chatDetail(chat.id), chat)
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
    },
  })
}

export function useAddChatMembers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ chatId, memberIds }: { chatId: string; memberIds: string[] }) =>
      messengerService.addMembers(chatId, memberIds),
    onSuccess: (chat) => {
      qc.setQueryData(messengerKeys.chatDetail(chat.id), chat)
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
    },
  })
}

export function useRemoveChatMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ chatId, userId }: { chatId: string; userId: string }) =>
      messengerService.removeMember(chatId, userId),
    onSuccess: (chat, { chatId }) => {
      qc.setQueryData(messengerKeys.chatDetail(chatId), chat)
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
    },
  })
}

/** Timestamp-only on the caller's membership — never touches ChatMessage
 * rows. Invalidating messages(chatId) is what makes the thread refetch
 * empty (listMessages already filters `createdAt > clearedAt` server-side). */
export function useClearChat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (chatId: string) => messengerService.clearChat(chatId),
    onSuccess: (chat, chatId) => {
      qc.setQueryData(messengerKeys.chatDetail(chatId), chat)
      void qc.invalidateQueries({ queryKey: messengerKeys.messages(chatId) })
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
      void qc.invalidateQueries({ queryKey: messengerKeys.unread() })
    },
  })
}

/** Hides the chat from the caller's own inbox (still a member) — reappears
 * on its own the moment a new message lands, so nothing here needs a
 * client-side "undo"; just drop it from every cached chats() list now. */
export function useDeleteChat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (chatId: string) => messengerService.deleteChat(chatId),
    onSuccess: (_void, chatId) => {
      qc.setQueriesData(
        { queryKey: messengerKeys.chats() },
        (old: CursorPage<ChatListItem> | undefined) => {
          if (!old) return old
          if (!old.items.some((c) => c.id === chatId)) return old
          return { ...old, items: old.items.filter((c) => c.id !== chatId) }
        },
      )
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
      void qc.invalidateQueries({ queryKey: messengerKeys.unread() })
    },
  })
}

/** Appends the sent message to the newest (first) page immediately — same
 * "optimistic-ish, real response, no reload" feel as useAddTaskComment. */
export function useSendChatMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ chatId, input }: { chatId: string; input: SendMessageInput }) =>
      messengerService.sendMessage(chatId, input),
    onSuccess: (message, { chatId }) => {
      qc.setQueryData(
        messengerKeys.messages(chatId),
        (old: { pages: CursorPage<ChatMessage>[]; pageParams: unknown[] } | undefined) => {
          const first = old?.pages[0]
          if (!first) return old
          if (first.items.some((m) => m.id === message.id)) return old
          const pages = [...old.pages]
          pages[0] = { ...first, items: [...first.items, message], nextCursor: first.nextCursor ?? null }
          return { ...old, pages }
        },
      )
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
    },
  })
}

export function useDeleteChatMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ chatId, messageId, forEveryone }: { chatId: string; messageId: string; forEveryone: boolean }) =>
      messengerService.deleteMessage(chatId, messageId, forEveryone),
    onSuccess: (_void, { chatId }) => {
      void qc.invalidateQueries({ queryKey: messengerKeys.messages(chatId) })
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
    },
  })
}

export function useMarkChatSeen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ chatId, messageIds }: { chatId: string; messageIds: string[] }) =>
      messengerService.markSeen(chatId, messageIds),
    onSuccess: (_void, { chatId }) => {
      // Zero the list badge immediately — don't wait on a refetch. Matches
      // every cached chatsList(params) variant (search/unread filters),
      // not just whichever one the inbox happens to be showing right now.
      qc.setQueriesData(
        { queryKey: messengerKeys.chats() },
        (old: CursorPage<ChatListItem> | undefined) => {
          if (!old) return old
          if (!old.items.some((c) => c.id === chatId && c.unreadCount > 0)) return old
          return { ...old, items: old.items.map((c) => (c.id === chatId ? { ...c, unreadCount: 0 } : c)) }
        },
      )
      // Safety net (a stale filtered list, another tab, etc.) — and the
      // one thing that actually drops the header badge number.
      void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
      void qc.invalidateQueries({ queryKey: messengerKeys.unread() })
    },
  })
}

function patchMessageInCache(
  qc: ReturnType<typeof useQueryClient>,
  chatId: string,
  message: ChatMessage,
) {
  qc.setQueryData(
    messengerKeys.messages(chatId),
    (old: { pages: CursorPage<ChatMessage>[]; pageParams: unknown[] } | undefined) => {
      if (!old) return old
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          items: page.items.map((m) => (m.id === message.id ? message : m)),
        })),
      }
    },
  )
}

/** Optimistic toggle — mirrors the Tasks Discussion reaction hook exactly
 * (see optimisticToggleReactions in tasks/hooks/useTasks.ts). */
function optimisticToggleReactions(
  reactions: ChatReactionSummary[],
  emoji: string,
  me: ChatUserRef,
): ChatReactionSummary[] {
  const wasMine = reactions.find((r) => r.mine)?.emoji
  const withoutMe = reactions
    .map((r) => (r.mine ? { ...r, mine: false, count: r.count - 1, users: r.users.filter((u) => u.id !== me.id) } : r))
    .filter((r) => r.count > 0)
  if (wasMine === emoji) return withoutMe
  const existingIndex = withoutMe.findIndex((r) => r.emoji === emoji)
  if (existingIndex === -1) return [...withoutMe, { emoji, count: 1, mine: true, users: [me] }]
  return withoutMe.map((r, i) =>
    i === existingIndex ? { ...r, mine: true, count: r.count + 1, users: [...r.users, me] } : r,
  )
}

/** Second step of send: attaches audio to an already-created voice-stub
 * message. Separate mutation so a failed upload can be retried without
 * creating a duplicate message — same split as useUploadTaskVoice. */
export function useUploadChatVoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      chatId,
      messageId,
      blob,
      mimeType,
    }: {
      chatId: string
      messageId: string
      blob: Blob
      mimeType: string
    }) => messengerService.uploadMessageVoice(chatId, messageId, blob, mimeType),
    onSuccess: (message, { chatId }) => {
      // Flip voiceAvailable immediately so other viewers / this tab don't
      // stay stuck on the "device-only" fallback until a slow refetch.
      patchMessageInCache(qc, chatId, { ...message, voiceAvailable: true })
      void qc.invalidateQueries({ queryKey: messengerKeys.messages(chatId) })
    },
  })
}

/** Listener pressed play — patches this viewer's own cache immediately (so
 * the mic badge/waveform turn blue without waiting for the next poll). */
export function useMarkChatVoiceOpened() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ chatId, messageId }: { chatId: string; messageId: string }) =>
      messengerService.markVoiceOpened(chatId, messageId),
    onSuccess: (_void, { chatId, messageId }) => {
      qc.setQueryData(
        messengerKeys.messages(chatId),
        (old: { pages: CursorPage<ChatMessage>[]; pageParams: unknown[] } | undefined) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((m) => (m.id === messageId ? { ...m, voiceOpened: true } : m)),
            })),
          }
        },
      )
    },
  })
}

export function useToggleChatReaction() {
  const qc = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: ({ chatId, messageId, emoji }: { chatId: string; messageId: string; emoji: string }) =>
      messengerService.toggleReaction(chatId, messageId, emoji),
    onMutate: async ({ chatId, messageId, emoji }) => {
      if (!user) return undefined
      await qc.cancelQueries({ queryKey: messengerKeys.messages(chatId) })
      const previous = qc.getQueryData(messengerKeys.messages(chatId))
      qc.setQueryData(
        messengerKeys.messages(chatId),
        (old: { pages: CursorPage<ChatMessage>[]; pageParams: unknown[] } | undefined) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((m) =>
                m.id === messageId ? { ...m, reactions: optimisticToggleReactions(m.reactions, emoji, user) } : m,
              ),
            })),
          }
        },
      )
      return { previous }
    },
    onError: (_err, { chatId }, context) => {
      if (context?.previous) qc.setQueryData(messengerKeys.messages(chatId), context.previous)
    },
    onSuccess: (message, { chatId }) => patchMessageInCache(qc, chatId, message),
  })
}
